import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import {
  getStripeKillswitchStatus,
  StripeKillswitchStatus,
} from "../../api/stripeKillswitchAPI";
import { getTenantId } from "../../api/apiHelpers";

/**
 * Persistent banner shown at the top of admin/staff pages whenever the
 * Stripe killswitch is engaged for the current tenant. Visible on EVERY
 * page so staff can't forget that payments are off — there's no way to
 * silently break billing for an extended period.
 *
 * Polls every 60s so a flip from another browser (or the platform side)
 * surfaces without requiring a reload.
 */

const StripeKillswitchBanner: React.FC = () => {
  const [status, setStatus] = useState<StripeKillswitchStatus | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken") || "";
    if (!token) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const fetchStatus = async () => {
      try {
        const res = await getStripeKillswitchStatus(token);
        if (!cancelled && res.success && res.data) {
          setStatus(res.data);
        }
      } catch {
        // Silent — banner is non-essential. If the call fails we just
        // don't show anything; the next poll will retry.
      }
    };

    fetchStatus();
    timer = setInterval(fetchStatus, 60_000);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  if (!status?.disabled) return null;

  const tenantId = getTenantId();
  const settingsLink = tenantId
    ? `/${tenantId}/manage/settings/stripe-killswitch`
    : "#";

  return (
    <div className="bg-red-600 text-white px-4 py-2 text-sm flex items-center gap-2 sticky top-0 z-50">
      <ShieldAlert className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <strong>Stripe is disabled.</strong> Online payments, autopay, and invoice
        generation are paused.
        {status.reason ? (
          <>
            {" "}
            <span className="italic">{status.reason}</span>
          </>
        ) : null}
      </div>
      <Link
        to={settingsLink}
        className="underline whitespace-nowrap hover:text-red-100"
      >
        Manage
      </Link>
    </div>
  );
};

export default StripeKillswitchBanner;
